import {
  AST,
  Expression,
  Macro,
  Relation,
  Ty,
  Values,
  From,
} from '@cotera/era';
import _ from 'lodash';
import type {
  RuntimeData,
  AsyncFnRuntimeDataProvider,
  RuntimeDataProvider,
} from './runtime-data';
import { Assert } from '@cotera/utilities';
import {
  ENTITY_DEFINITIONS,
  EVENT_CURSORS,
  MANIFEST_EVENT_STREAM_SINKS,
  MANIFEST_EVENT_STREAMS,
} from './vars';
import { systemTablesForWriteSchema } from './system-tables';
import { DG } from '../dg';
import { ToolInfo } from '../dg/graph/graph';

export type ToolResolver = {
  isToolAvailable: (toolKey: string) => boolean;
  getToolInfo: (toolKey: string) => ToolInfo | null;
  list: () => string[];
};

export const applyToRelAsync = async <T>(
  input: Relation,
  provider: AsyncFnRuntimeDataProvider<T>,
  args: T,
  toolResolver: ToolResolver
): Promise<Relation> => {
  let rel = input;
  const needs: string[][] = [];
  let counter = 0;
  while (counter < 5) {
    counter++;
    const neededScopes = Object.keys(rel.freeVars);

    needs.push(neededScopes);

    if (neededScopes.length === 0 || counter > 5) {
      break;
    }

    const scope = await provideAsync(
      neededScopes,
      provider,
      args,
      toolResolver
    );

    rel = scope.reduce(
      (r, { scope, vars }) => Macro.callRelMacro(r.ast, vars, { scope }),
      rel
    );
  }
  return rel;
};

const RUNTIME_DATA_TO_SCOPE_VARS = (
  toolResolver: ToolResolver
): {
  [Scope in keyof RuntimeData]: (
    data: RuntimeData[Scope]
  ) => Record<string, Expression | Relation>;
} => ({
  '@@cotera-graph-refs': ({ commits }) => {
    const res = DG.GraphCompiler.fromCommits(commits, toolResolver)
      .compile()
      .match(
        (x) => x,
        (x) => x.lastGoodBuild
      );

    const relations = Object.entries(res.graph).flatMap(
      ([name, res]): [string, Relation][] =>
        res.match(
          (x) => (x.item.t === 'rel' ? [[name, x.item.rel]] : []),
          () => []
        )
    );

    return Object.fromEntries(relations);
  },
  '@@cotera-system': ({ WRITE_SCHEMA }) => {
    const systemTables = systemTablesForWriteSchema(WRITE_SCHEMA);
    return _.mapValues(systemTables, (table) => From(table));
  },
  '@@cotera-entities': ({ DEFINITIONS, WRITE_SCHEMA }) => {
    const { stable_ids } = systemTablesForWriteSchema(WRITE_SCHEMA);

    const stableIdsForEntity = Object.fromEntries(
      DEFINITIONS.map(({ name }) => [
        `${name}-stable-ids`,
        From(stable_ids).where((t) => t.attr('definition_id').eq(name)),
      ])
    );

    return {
      DEFINITIONS: StrictValues(DEFINITIONS, ENTITY_DEFINITIONS.attributes),
      ...stableIdsForEntity,
    };
  },
  '@@cotera-events': (data) => ({
    CURSORS: StrictValues(data.CURSORS, EVENT_CURSORS.attributes),
  }),
  '@@cotera-manifest-event-streams': ({ SINKS, STREAMS }) => ({
    STREAMS: StrictValues(STREAMS, MANIFEST_EVENT_STREAMS.attributes),
    SINKS: StrictValues(SINKS, MANIFEST_EVENT_STREAM_SINKS.attributes),
  }),
});

const buildScopeVars = (
  vars: Record<string, Expression | Relation>
): AST.MacroVarReplacements<AST.RelMacroChildren> =>
  Object.entries(vars).reduce<AST.MacroVarReplacements<AST.RelMacroChildren>>(
    (scopeVars, [name, replacement]) => {
      if (replacement instanceof Expression) {
        return {
          ...scopeVars,
          exprs: { ...scopeVars.exprs, [name]: replacement.ast },
        };
      } else if (replacement instanceof Relation) {
        return {
          ...scopeVars,
          rels: { ...scopeVars.rels, [name]: replacement.ast },
        };
      } else {
        return Assert.unreachable(replacement);
      }
    },
    { exprs: {}, rels: {}, sections: {} }
  );

export const provide = (
  provider: RuntimeDataProvider,
  toolResolver: ToolResolver
) => {
  return _.mapValues(
    provider,
    (res, name): AST.MacroVarReplacements<AST.RelMacroChildren> =>
      buildScopeVars(
        RUNTIME_DATA_TO_SCOPE_VARS(toolResolver)[
          name as keyof RuntimeDataProvider
        ](res as any)
      )
  );
};

const provideAsync = async <T>(
  neededScopes: string[],
  provider: AsyncFnRuntimeDataProvider<T>,
  args: T,
  toolResolver: ToolResolver
): Promise<
  { scope: string; vars: AST.MacroVarReplacements<AST.RelMacroChildren> }[]
> => {
  const dataPromises: (() => Promise<{
    scope: string;
    vars: AST.MacroVarReplacements<AST.RelMacroChildren>;
  }>)[] = [];

  const providedScopes = Object.keys(
    provider
  ) as (keyof AsyncFnRuntimeDataProvider<T>)[];

  for (const scope of providedScopes.filter((scope) =>
    neededScopes.includes(scope)
  )) {
    dataPromises.push(async () => {
      const data = await provider[scope](args);
      // This is safe, TS just doesn't know we're calling the same scope on both providers;
      // thus the `as any`
      const mapped = RUNTIME_DATA_TO_SCOPE_VARS(toolResolver)[scope](
        data as any
      );
      return { scope, vars: buildScopeVars(mapped) };
    });
  }

  return Promise.all(dataPromises.map((f) => f()));
};

const StrictValues = (
  rows: readonly Ty.Row[],
  attributes: Record<string, Ty.Shorthand>
): Relation =>
  Values(
    rows.map((row) => _.pick(row, Object.keys(attributes))),
    attributes
  );
